import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Shield, Home, Truck, Package, MapPin, 
  RefreshCw, ExternalLink, Search, ChevronDown, ChevronUp, Filter, Copy, Download, Mail
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

const STATUS_CONFIG = {
  'pre_transit': { label: 'LABEL CREATED', color: 'text-blue-400', bg: 'bg-blue-500/20', rowBg: 'bg-blue-900/30 border-blue-800/50' },
  'in_transit': { label: 'IN TRANSIT', color: 'text-tiffany-500', bg: 'bg-tiffany-500/20', rowBg: 'bg-tiffany-900/30 border-tiffany-800/50' },
  'out_for_delivery': { label: 'OUT FOR DELIVERY', color: 'text-orange-400', bg: 'bg-orange-500/20', rowBg: 'bg-orange-900/30 border-orange-800/50' },
  'delivered': { label: 'DELIVERED', color: 'text-green-500', bg: 'bg-green-500/20', rowBg: 'bg-green-900/30 border-green-800/50' },
  'return_to_sender': { label: 'RETURNED', color: 'text-red-400', bg: 'bg-red-500/20', rowBg: 'bg-red-900/30 border-red-800/50' },
  'failure': { label: 'FAILED', color: 'text-red-500', bg: 'bg-red-500/20', rowBg: 'bg-red-900/30 border-red-800/50' },
  'shipped': { label: 'SHIPPED', color: 'text-tiffany-500', bg: 'bg-tiffany-500/20', rowBg: 'bg-tiffany-900/30 border-tiffany-800/50' },
  'pending_shipment': { label: 'PENDING SHIPMENT', color: 'text-yellow-400', bg: 'bg-yellow-500/20', rowBg: 'bg-yellow-900/30 border-yellow-800/50' },
  'expired': { label: 'EXPIRED', color: 'text-red-500', bg: 'bg-red-500/20', rowBg: 'bg-red-900/30 border-red-800/50' },
  'unknown': { label: 'UNKNOWN', color: 'text-zinc-400', bg: 'bg-zinc-500/20', rowBg: 'bg-zinc-800/50 border-zinc-700/50' },
};

const CARRIER_URLS = {
  'USPS': 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  'UPS': 'https://www.ups.com/track?tracknum=',
  'FEDEX': 'https://www.fedex.com/fedextrack/?trknbr=',
  'usps': 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
};

const EASYPOST_TRACKER_URL = 'https://track.easypost.com/';

// Encode tracker ID for EasyPost public URL (v1:tracker_id -> base64)
const encodeTrackerId = (trackerId) => {
  if (!trackerId) return null;
  try {
    const payload = `v1:${trackerId}`;
    return btoa(payload);
  } catch (e) {
    console.error('Failed to encode tracker ID:', e);
    return trackerId;
  }
};

export default function AdminShipments() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDelivered, setShowDelivered] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [refreshingOrder, setRefreshingOrder] = useState(null);
  const [copiedTracking, setCopiedTracking] = useState(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [autoRefreshProgress, setAutoRefreshProgress] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [pendingShipmentCount, setPendingShipmentCount] = useState(0);
  const [debugModal, setDebugModal] = useState(null); // { orderId, debug } or null

  // Export orders to PirateShip CSV
  const exportToPirateShip = async (shippingType = 'all') => {
    setExportingCsv(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API}/admin/orders/export/pirateship?status=pending_fulfillment&shipping=${shippingType}`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `pirateship_${shippingType}_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${shippingType.toUpperCase()} CSV EXPORTED`);
    } catch (error) {
      console.error('Export failed:', error);
      if (error.response?.status === 404) {
        toast.error(`NO ${shippingType.toUpperCase()} ORDERS READY TO SHIP`);
      } else {
        toast.error('EXPORT FAILED');
      }
    } finally {
      setExportingCsv(false);
    }
  };

  // Fetch count of orders pending fulfillment
  const fetchPendingCount = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API}/admin/orders/export/pirateship/preview?status=pending_fulfillment&limit=1`, { headers });
      setPendingShipmentCount(response.data.total_exportable || 0);
    } catch (error) {
      console.error('Failed to fetch pending count:', error);
    }
  }, [getAuthHeader]);

  const copyTrackingNumber = async (trackingNumber) => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopiedTracking(trackingNumber);
      setTimeout(() => setCopiedTracking(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      if (!headers.Authorization) return;
      
      const res = await axios.get(`${API}/admin/shipped-orders`, { headers });
      setOrders(res.data.orders || []);
      return res.data.orders || [];
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Auto-refresh tracking for all orders with tracking numbers on page load
  const refreshAllTracking = useCallback(async (ordersList) => {
    const headers = getAuthHeader();
    if (!headers || !headers.Authorization) {
      console.log('Auto-refresh: No auth header, skipping');
      return;
    }
    
    // Refresh ALL orders that have tracking numbers (including delivered to update location)
    const ordersToRefresh = ordersList.filter(o => o.tracking_number);
    
    console.log(`Auto-refresh: Found ${ordersToRefresh.length} orders with tracking numbers`);
    
    if (ordersToRefresh.length === 0) return;
    
    setIsAutoRefreshing(true);
    setAutoRefreshProgress(`0/${ordersToRefresh.length}`);
    
    // Refresh in parallel (max 5 at a time to avoid overwhelming the API)
    const batchSize = 5;
    let refreshedCount = 0;
    for (let i = 0; i < ordersToRefresh.length; i += batchSize) {
      const batch = ordersToRefresh.slice(i, i + batchSize);
      await Promise.all(
        batch.map(order => 
          axios.post(`${API}/admin/orders/${order.order_id}/refresh-tracking`, {}, { headers })
            .then(() => { refreshedCount++; })
            .catch(err => console.error(`Failed to refresh ${order.order_id}:`, err))
        )
      );
      setAutoRefreshProgress(`${Math.min(i + batchSize, ordersToRefresh.length)}/${ordersToRefresh.length}`);
    }
    
    console.log(`Auto-refresh: Completed ${refreshedCount}/${ordersToRefresh.length} orders`);
    
    // Refetch orders to get updated data
    await fetchOrders();
    setIsAutoRefreshing(false);
    setAutoRefreshProgress('');
  }, [getAuthHeader, fetchOrders]);

  // Track if we've already auto-refreshed
  const [hasAutoRefreshed, setHasAutoRefreshed] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchPendingCount();
  }, [fetchOrders, fetchPendingCount]);

  // Auto-refresh tracking data ONCE after initial load
  useEffect(() => {
    if (orders.length > 0 && !loading && !hasAutoRefreshed) {
      console.log(`Auto-refresh: Triggering for ${orders.length} orders`);
      setHasAutoRefreshed(true);
      refreshAllTracking(orders);
    }
  }, [orders, loading, hasAutoRefreshed, refreshAllTracking]);

  // Filter orders based on search and showDelivered toggle
  useEffect(() => {
    let filtered = orders;
    
    // Filter out delivered if toggle is off
    if (!showDelivered) {
      filtered = filtered.filter(o => 
        o.status !== 'delivered' && o.easypost_status !== 'delivered'
      );
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(o => 
        o.order_id?.toLowerCase().includes(query) ||
        o.customer_name?.toLowerCase().includes(query) ||
        o.status?.toLowerCase().includes(query) ||
        o.easypost_status?.toLowerCase().includes(query)
      );
    }
    
    // Sort by order number (descending - newest first)
    filtered.sort((a, b) => {
      const numA = parseInt(a.order_id?.replace('BLK-', '') || '0');
      const numB = parseInt(b.order_id?.replace('BLK-', '') || '0');
      return numB - numA;
    });
    
    setFilteredOrders(filtered);
  }, [orders, searchQuery, showDelivered]);

  const toggleExpanded = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const refreshTracking = async (orderId) => {
    setRefreshingOrder(orderId);
    try {
      const headers = getAuthHeader();
      const response = await axios.post(`${API}/admin/orders/${orderId}/refresh-tracking`, {}, { headers });
      
      // Show debug info in styled modal
      if (response.data.debug) {
        setDebugModal({ orderId, debug: response.data.debug });
      }
      
      await fetchOrders();
    } catch (error) {
      console.error('Failed to refresh tracking:', error);
      toast.error(`Failed to refresh ${orderId}: ${error.message}`);
    } finally {
      setRefreshingOrder(null);
    }
  };

  const resendTrackingEmail = async (orderId, recipientEmail) => {
    if (!window.confirm(`Resend tracking email to ${recipientEmail || 'customer'}?`)) return;
    try {
      const headers = getAuthHeader();
      const response = await axios.post(`${API}/admin/orders/${orderId}/resend-tracking-email`, {}, { headers });
      toast.success(`Tracking email resent to ${response.data.recipient}`);
      await fetchOrders();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Resend failed';
      console.error('Failed to resend tracking email:', error);
      toast.error(`Resend failed: ${msg}`);
    }
  };

  const getStatusDisplay = (order) => {
    let status = order.easypost_status || order.status || 'unknown';
    
    // Always check latest_message to get the most accurate status
    // This handles cases where easypost_status hasn't been updated yet
    if (order.latest_message) {
      const msg = order.latest_message.toLowerCase();
      if (msg.includes('out for delivery')) {
        status = 'out_for_delivery';
      } else if (msg.includes('delivered')) {
        status = 'delivered';
      } else if (!order.easypost_status && (msg.includes('in transit') || msg.includes('arrived') || msg.includes('departed') || msg.includes('processed'))) {
        status = 'in_transit';
      }
    }
    
    const config = STATUS_CONFIG[status] || { label: status.toUpperCase().replace(/_/g, ' '), color: 'text-zinc-400', bg: 'bg-zinc-500/20' };
    return config;
  };

  const openTracker = (trackingNumber, carrier) => {
    const url = CARRIER_URLS[carrier] || CARRIER_URLS['USPS'];
    window.open(`${url}${trackingNumber}`, '_blank');
  };

  const openEasyPostTracker = (trackerId) => {
    if (trackerId) {
      const encodedId = encodeTrackerId(trackerId);
      window.open(`${EASYPOST_TRACKER_URL}${encodedId}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-tiffany-500 text-black rounded-none border-0 hidden sm:inline-flex">ADMIN</Badge>
          </Link>
          
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm">
            ← BACK TO ADMIN
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex items-center gap-3 mb-6">
          <Truck className="w-8 h-8 text-tiffany-500" />
          <h1 className="font-sans text-2xl sm:text-3xl text-white tracking-tighter">
            TRACK SHIPPING
          </h1>
        </div>

        {/* Search and Filters */}
        <div className="bg-zinc-900 border border-zinc-800 p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="SEARCH ORDER #, NAME, STATUS..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-zinc-500">SHOW DELIVERED</span>
              <Switch
                checked={showDelivered}
                onCheckedChange={setShowDelivered}
                className="data-[state=checked]:bg-tiffany-500"
              />
            </div>
            <Button
              onClick={fetchOrders}
              disabled={loading}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none font-mono text-xs"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              REFRESH
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-zinc-900 border border-zinc-800 p-3 mb-4 flex items-center justify-between">
          <span className="font-mono text-sm text-zinc-400">
            {isAutoRefreshing ? (
              <>
                <RefreshCw className="w-4 h-4 inline animate-spin mr-2 text-tiffany-500" />
                <span className="text-tiffany-500">REFRESHING ALL TRACKING... {autoRefreshProgress}</span>
              </>
            ) : (
              <>
                SHOWING <span className="text-white">{filteredOrders.length}</span> OF <span className="text-white">{orders.length}</span> SHIPMENTS
              </>
            )}
          </span>
        </div>

        {/* Orders List */}
        {loading && orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <RefreshCw className="w-12 h-12 text-tiffany-500 animate-spin mb-4" />
            <p className="font-mono text-zinc-500">LOADING SHIPMENTS...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border border-zinc-800">
            <Package className="w-16 h-16 text-zinc-700 mb-4" />
            <p className="font-mono text-zinc-500 text-lg">NO SHIPMENTS FOUND</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredOrders.map((order) => {
              const statusConfig = getStatusDisplay(order);
              const isExpanded = expandedOrders[order.order_id];
              
              return (
                <div 
                  key={order.order_id} 
                  className={`border ${statusConfig.rowBg || 'bg-zinc-900 border-zinc-800'}`}
                >
                  {/* Order Row */}
                  <div 
                    className="p-2 sm:p-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => toggleExpanded(order.order_id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-tiffany-500 text-sm">{order.order_id}</p>
                          <p className="font-mono text-white text-sm uppercase">{order.customer_name}</p>
                          {(order.latest_message || order.latest_location) && (
                            <div className="mt-1">
                              {order.latest_message && (
                                <p className="font-mono text-zinc-400 text-[10px] uppercase">
                                  {order.latest_message}
                                </p>
                              )}
                              {order.latest_location && (
                                <p className="font-mono text-zinc-500 text-[10px] uppercase">
                                  {order.latest_location}
                                </p>
                              )}
                            </div>
                          )}
                          {order.easypost_tracker_id && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEasyPostTracker(order.easypost_tracker_id);
                              }}
                              size="sm"
                              variant="outline"
                              className="border-zinc-500 text-zinc-300 hover:bg-zinc-700 rounded-none p-1 h-5 w-5 mt-1"
                              title="EasyPost tracker"
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Track Button */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {order.tracking_number && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTracker(order.tracking_number, order.carrier);
                            }}
                            size="sm"
                            className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-mono text-xs"
                            title="Track on carrier site"
                          >
                            <Truck className="w-3 h-3 mr-1" />
                            TRACK
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-zinc-800 p-4 bg-zinc-800/30">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="font-mono text-zinc-500 text-[10px] mb-1">TRACKING NUMBER</p>
                          {order.tracking_number ? (
                            <button
                              onClick={() => copyTrackingNumber(order.tracking_number)}
                              className="font-mono text-white text-sm flex items-center gap-2 hover:text-tiffany-400 transition-colors"
                            >
                              {order.tracking_number}
                              <Copy className="w-3 h-3" />
                              {copiedTracking === order.tracking_number && (
                                <span className="text-tiffany-500 text-[10px]">COPIED!</span>
                              )}
                            </button>
                          ) : (
                            <p className="font-mono text-white text-sm">N/A</p>
                          )}
                        </div>
                        <div>
                          <p className="font-mono text-zinc-500 text-[10px] mb-1">CARRIER</p>
                          <p className="font-mono text-white text-sm">{(order.carrier || 'USPS').toUpperCase()}</p>
                        </div>
                        <div>
                          <p className="font-mono text-zinc-500 text-[10px] mb-1">SHIPPED DATE</p>
                          <p className="font-mono text-white text-sm">
                            {order.shipped_at ? new Date(order.shipped_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="font-mono text-zinc-500 text-[10px] mb-1">LAST UPDATE</p>
                          <p className="font-mono text-white text-sm">
                            {order.updated_at ? new Date(order.updated_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {order.latest_location && (
                        <div className="mb-4">
                          <p className="font-mono text-zinc-500 text-[10px] mb-1">LOCATION</p>
                          <p className="font-mono text-white text-sm flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {order.latest_location}
                          </p>
                        </div>
                      )}

                      {/* Items */}
                      {order.items && order.items.length > 0 && (
                        <div className="mb-4">
                          <p className="font-mono text-zinc-500 text-[10px] mb-2">ITEMS</p>
                          <div className="space-y-1">
                            {order.items.map((item, idx) => (
                              <p key={idx} className="font-mono text-zinc-300 text-xs">
                                {item.quantity}x {item.name}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {order.tracking_number && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTracker(order.tracking_number, order.carrier);
                            }}
                            size="sm"
                            className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-mono text-xs"
                          >
                            <Truck className="w-3 h-3 mr-1" />
                            TRACK
                          </Button>
                        )}
                        {order.easypost_tracker_id && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEasyPostTracker(order.easypost_tracker_id);
                            }}
                            size="sm"
                            variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none font-mono text-xs"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            EASYPOST
                          </Button>
                        )}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            refreshTracking(order.order_id);
                          }}
                          disabled={refreshingOrder === order.order_id}
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none font-mono text-xs"
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${refreshingOrder === order.order_id ? 'animate-spin' : ''}`} />
                          REFRESH STATUS
                        </Button>
                        {order.tracking_number && (order.customer_email || order.user_email) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              resendTrackingEmail(order.order_id, order.customer_email || order.user_email);
                            }}
                            size="sm"
                            variant="outline"
                            className="border-tiffany-700 text-tiffany-300 hover:bg-tiffany-900/30 rounded-none font-mono text-xs"
                            data-testid={`resend-tracking-email-${order.order_id}`}
                            title={order.tracking_email_sent_at
                              ? `Last sent: ${new Date(order.tracking_email_sent_at).toLocaleString()}`
                              : 'No confirmed send on record — click to send'}
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            {order.tracking_email_sent_at ? 'RESEND EMAIL' : 'SEND EMAIL'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Debug Tracking Refresh Modal */}
      {debugModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDebugModal(null)}
          data-testid="refresh-debug-modal"
        >
          <div
            className="bg-[#0a0a0a] border border-tiffany-500/40 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-zinc-800 p-3 bg-tiffany-500/5">
              <div className="flex items-center gap-2 min-w-0">
                <RefreshCw className="w-4 h-4 text-tiffany-500 flex-shrink-0" />
                <h2 className="font-mono text-sm text-tiffany-500 truncate">REFRESH {debugModal.orderId}</h2>
              </div>
              <button
                onClick={() => setDebugModal(null)}
                className="text-zinc-500 hover:text-white font-mono text-xs"
                data-testid="refresh-debug-close"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-2">
              {[
                { label: 'STATUS', value: (debugModal.debug.tracker_status || 'N/A').toUpperCase(), accent: 'text-white' },
                { label: 'EVENTS', value: `${debugModal.debug.tracking_details_count || 0}`, accent: 'text-white' },
                { label: 'MESSAGE', value: debugModal.debug.latest_message || 'NONE', accent: 'text-white' },
                { label: 'LOCATION', value: debugModal.debug.latest_location || 'NONE', accent: 'text-white' },
                { label: 'HAD TRACKER', value: debugModal.debug.had_existing_tracker_id ? 'YES' : 'NO', accent: debugModal.debug.had_existing_tracker_id ? 'text-tiffany-500' : 'text-zinc-500' },
                { label: 'NEW TRACKER', value: debugModal.debug.new_tracker_created ? 'YES' : 'N/A', accent: debugModal.debug.new_tracker_created ? 'text-orange-400' : 'text-zinc-500' },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-3 py-1 border-b border-zinc-900 last:border-b-0">
                  <span className="font-mono text-[10px] text-zinc-500 w-24 flex-shrink-0 pt-0.5">{row.label}</span>
                  <span className={`font-mono text-xs ${row.accent} break-words flex-1`}>{row.value}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-800 p-3">
              <Button
                onClick={() => setDebugModal(null)}
                className="w-full bg-tiffany-500 hover:bg-tiffany-600 text-black font-mono text-xs rounded-none h-9"
                data-testid="refresh-debug-close-btn"
              >
                CLOSE
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
